import ENDPOINTS from 'tradera-constants/endpoints';
import {
    axiosWithTokenRefresh,
    checkResponseVersion,
    finalizeResponse,
    handleError,
    utilizeCancelToken,
    axiosConfigs
} from 'tradera-utils/api';
import initData from 'init-data';
import { toLocalizedUrl } from 'tradera-utils/url';
import { getLanguage } from 'tradera-apps/syi/script/app_react/utils/language';

// Prevents URL:s that begins with // when that was not intended.
const getSafeUrl = (baseUrl, url) => {
    if (baseUrl.endsWith('/') && url.startsWith('/')) {
        return baseUrl + url.substring(1);
    }
    return baseUrl + url;
};

const httpClient = (baseUrl, shouldLocalizeUrl) => {
    const axiosWrapper = (url, httpClientConfig, axiosCaller) => {
        let { cancelTokenId, ...axiosConfig } = httpClientConfig;
        const version = initData.version;
        if (cancelTokenId) {
            const { cancel, cancelToken } = utilizeCancelToken(cancelTokenId);
            axiosConfig.cancelToken = cancelToken;
            if (cancel) {
                cancel();
            }
        }
        const safeUrl = getSafeUrl(baseUrl, url);
        const localizedUrl = shouldLocalizeUrl
            ? toLocalizedUrl(safeUrl, getLanguage())
            : safeUrl;
        return axiosCaller(axiosWithTokenRefresh(), localizedUrl, axiosConfig)
            .then(
                version ? checkResponseVersion(version) : response => response
            )
            .then(finalizeResponse())
            .catch(handleError());
    };

    return {
        get: (url, httpClientConfig = axiosConfigs.authenticated) => {
            return axiosWrapper(url, httpClientConfig, (axios, url, config) =>
                axios.get(url, config)
            );
        },
        post: (url, payload, httpClientConfig = axiosConfigs.authenticated) => {
            return axiosWrapper(url, httpClientConfig, (axios, url, config) =>
                axios.post(url, payload, config)
            );
        },
        put: (url, payload, httpClientConfig = axiosConfigs.authenticated) => {
            return axiosWrapper(url, httpClientConfig, (axios, url, config) =>
                axios.put(url, payload, config)
            );
        }
    };
};

const trpcClient = endpoint => {
    const client = httpClient(`/api/${endpoint}`, false);
    const unpackResponse = ({ data }) => data;
    return {
        command: (commandName, payload, httpClientConfig) =>
            client
                .post(`/commands/${commandName}`, payload, httpClientConfig)
                .then(unpackResponse),
        query: (queryName, httpClientConfig) =>
            client
                .get(`/queries/${queryName}`, httpClientConfig)
                .then(unpackResponse)
    };
};

export const defaultClient = httpClient('');
export const touchWebClient = httpClient('/', true);
export const webApiClient = httpClient(ENDPOINTS.WEB_API);
export const cmsApiClient = httpClient(ENDPOINTS.CMS_API);
export const marketingApiClient = httpClient(ENDPOINTS.MARKETING_PUBLIC_API);
export const memberIdentificationClient = trpcClient('member-identification');
export const translationOnDemandClient = trpcClient('translation-on-demand');
